import { authServiceApi } from "../../../../app/authServiceApi";
import { API_URL } from "../../../../constants/apiUrl";
import { CommonResponse, GetQueryArgsType } from "../../../../types";
import {
  AllShiftParams,
  AllShiftResponse,
  GetShiftDetailsMemberOverviewResponse,
  GetShiftDetailsOverviewResponse,
  GetShiftMembersResponse,
  SingleShiftResponse,
} from "./shiftManagement.type";

export const shiftManagementApi = authServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // ------ Mutations ------
    createShift: builder.mutation({
      query: (data) => ({
        url: API_URL.createShift,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetShift"];
        return [];
      },
    }),

    updateShift: builder.mutation({
      query: ({ data, id }) => ({
        url: `${API_URL.updateShift}/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, _error, arg) => {
        if (result) {
          return ["GetShift", { type: "GetShift", id: arg.id }];
        }
        return [];
      },
    }),

    updateShiftGraceTime: builder.mutation({
      query: ({ data }) => ({
        url: `${API_URL.updateShiftGraceTime}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, _error, arg) => {
        if (result) {
          return ["GetShift", { type: "GetShift", id: arg.id }];
        }
        return [];
      },
    }),

    resetShiftGraceTime: builder.mutation({
      query: () => ({
        url: `${API_URL.resetShiftGraceTime}`,
        method: "PATCH",
      }),
      invalidatesTags: (result, _error, arg) => {
        if (result) {
          return ["GetShift", { type: "GetShift", id: arg.id }];
        }
        return [];
      },
    }),

    // ------ Queries ------
    getAllShift: builder.query<
      CommonResponse<AllShiftResponse>,
      GetQueryArgsType<AllShiftParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAllShift,
        ...queryArgs,
      }),
      providesTags: ["GetShift"],
    }),

    getSingleShift: builder.query<CommonResponse<SingleShiftResponse>, number>({
      query: (id) => ({
        url: `${API_URL.getSingleShift}/${id}`,
      }),
      providesTags: (_result, _error, id) => [{ type: "GetShift", id }],
    }),

    getShiftDetailsOverview: builder.query<
      CommonResponse<GetShiftDetailsOverviewResponse>,
      { id: number; args?: GetQueryArgsType<AllShiftParams> }
    >({
      query: ({ id, args }) => ({
        url: `${API_URL.getShiftDetailsOverview}/${id}`,
        ...args,
      }),
      providesTags: (_result, _error, args) => [
        { type: "GetShiftDetailsOverview", id: args.id },
      ],
    }),

    getShiftDetailsMemberOverview: builder.query<
      CommonResponse<GetShiftDetailsMemberOverviewResponse>,
      { id: number; args?: GetQueryArgsType<AllShiftParams> }
    >({
      query: ({ id, args }) => ({
        url: `${API_URL.getShiftDetailsMemberOverview}/${id}/members/overview`,
        ...args,
      }),
      providesTags: (_result, _error, args) => [
        { type: "GetShiftDetailsMemberOverview", id: args.id },
      ],
    }),

    getShiftMembers: builder.query<
      CommonResponse<GetShiftMembersResponse>,
      { id: number; args?: GetQueryArgsType<AllShiftParams> }
    >({
      query: ({ id, args }) => ({
        url: `${API_URL.getShiftMembers}/${id}/members/overview`,
        ...args,
      }),
      providesTags: (_result, _error, args) => [
        { type: "GetShiftMembers", id: args.id },
      ],
    }),
  }),
});

export const {
  // ------ Mutation Hooks ------
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useUpdateShiftGraceTimeMutation,
  useResetShiftGraceTimeMutation,

  // ------ Query Hooks ------
  useGetAllShiftQuery,
  useGetSingleShiftQuery,
  useGetShiftDetailsOverviewQuery,
  useGetShiftDetailsMemberOverviewQuery,
  useGetShiftMembersQuery,
} = shiftManagementApi;
